import { KYCVerificationStatus } from '../../banking/types/BankAccount';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { isFormerMember } from '../../common/utils/userUtils';
import { OnboardingStatus, UserType } from '../../user/types/User';
import {
    AuthorizationLevel,
    fullAccessToRestrictedCapabilities,
    LimitedAccessScope,
    noAccessToRestrictedCapabilities,
    type RestrictedCapabilityDictionary,
} from '../types/UserAuthorization';

interface UserAuthorizationInfo {
    authorizationLevel: AuthorizationLevel | null;
    limitedAccessScope: LimitedAccessScope | null;
    accessToRestrictedCapabilities: RestrictedCapabilityDictionary;
}

// We need to be able to restrict what some users can access in the app in a couple of
// different ways, based on various user/membership/etc attributes.
// This hook implements ABAC (Attribute Based Access Control) for that purpose.
// The hook reads the relevant attributes and assigns AuthorizationLevel, LimitedAccessScope,
// and notes what restricted capabilities the user has access to -- it also should react to
// changes to these attributes.
// The hook is intended to be used by a middleware redirect component that is active on our
// main app pages (the app/(app)/_layout), so authorization is applied to the core app.
export const useUserAuthorization = (): UserAuthorizationInfo => {
    // Step 1: Call the individual authorization hooks to see if they apply any restrictions
    // Each of these hooks react to changes to their relevant user attributes.
    const signupAuthorizationInfo = useUserSignupAuthorization();
    const membershipAuthorizationInfo = useUserMembershipAuthorization();
    const kycAuthorizationInfo = useUserKycAuthorization();
    const userTypeAuthorizationInfo = useUserTypeAuthorization();
    const membershipOnboardingAuthorization =
        useMembershipOnboardingAuthorization();
    const defaultAuthorizationInfo = {
        authorizationLevel: AuthorizationLevel.NORMAL_ACCESS,
        limitedAccessScope: LimitedAccessScope.FULL_ACCESS,
        accessToRestrictedCapabilities: fullAccessToRestrictedCapabilities,
    };

    // Step 2: As we get results from the inner authorization hooks, react to those here,
    // and apply cascading/prioritized logic so that we can control which authorizations
    // should take precedence over others (i.e. the most restrictive should take precedence).
    const authorizationInfo =
        signupAuthorizationInfo ??
        membershipAuthorizationInfo ??
        kycAuthorizationInfo ??
        userTypeAuthorizationInfo ??
        membershipOnboardingAuthorization ??
        defaultAuthorizationInfo;

    return {
        authorizationLevel: authorizationInfo.authorizationLevel,
        limitedAccessScope: authorizationInfo.limitedAccessScope,
        accessToRestrictedCapabilities:
            authorizationInfo.accessToRestrictedCapabilities,
    };
};

type UserAuthorizer = () => UserAuthorizationInfo | null;

const useUserSignupAuthorization: UserAuthorizer = () => {
    const user = useAuthedUser();

    const needsToCompleteSignupOnboarding = [
        OnboardingStatus.CONTACT_INFO,
    ].includes(user.onboardingStatus);

    const needsToCompleteSignupCheckout = [
        OnboardingStatus.GUIDE_SELECTION,
        OnboardingStatus.MEMBERSHIP,
        OnboardingStatus.CHECKOUT,
    ].includes(user.onboardingStatus);

    if (needsToCompleteSignupOnboarding || needsToCompleteSignupCheckout) {
        return {
            authorizationLevel: AuthorizationLevel.LIMITED_ACCESS,
            limitedAccessScope: needsToCompleteSignupOnboarding
                ? LimitedAccessScope.SIGNUP_ONBOARDING
                : LimitedAccessScope.SIGNUP_CHECKOUT,
            accessToRestrictedCapabilities: noAccessToRestrictedCapabilities,
        };
    }

    return null;
};

const useUserMembershipAuthorization: UserAuthorizer = () => {
    const user = useAuthedUser();

    const { isSubscriptionActive, membership, type: userType } = user;

    const isJointAccountHolder = userType === UserType.JOINT_ACCOUNT_HOLDER;
    const isMembershipActive = membership?.id != null && isSubscriptionActive;
    const needsAnActiveMembership =
        !isJointAccountHolder && !isMembershipActive;

    if (isFormerMember(user)) {
        return {
            authorizationLevel: AuthorizationLevel.LIMITED_ACCESS,
            limitedAccessScope: LimitedAccessScope.FORMER_MEMBER,
            accessToRestrictedCapabilities: noAccessToRestrictedCapabilities,
        };
    }

    if (needsAnActiveMembership) {
        return {
            authorizationLevel: AuthorizationLevel.NO_ACCESS,
            limitedAccessScope: LimitedAccessScope.NO_ACCESS,
            accessToRestrictedCapabilities: noAccessToRestrictedCapabilities,
        };
    }

    return null;
};

const useUserKycAuthorization: UserAuthorizer = () => {
    const user = useAuthedUser();
    const { onboardingStatus, syncteraKycStatus, type: userType } = user;

    const isJointAccountHolder = userType === UserType.JOINT_ACCOUNT_HOLDER;
    const hasCompletedKyc =
        syncteraKycStatus === KYCVerificationStatus.ACCEPTED;

    const needsToCompleteKyc =
        (isJointAccountHolder && !hasCompletedKyc) ||
        onboardingStatus === OnboardingStatus.JOINT_KYC_CONTACT_INFO;

    if (needsToCompleteKyc) {
        return {
            authorizationLevel: AuthorizationLevel.LIMITED_ACCESS,
            limitedAccessScope: LimitedAccessScope.KYC,
            accessToRestrictedCapabilities: noAccessToRestrictedCapabilities,
        };
    }

    return null;
};

const useUserTypeAuthorization: UserAuthorizer = () => {
    const user = useAuthedUser();

    const isJointAccountHolder = user.type === UserType.JOINT_ACCOUNT_HOLDER;
    const needsUserTypeRestrictions = isJointAccountHolder;

    if (needsUserTypeRestrictions) {
        return {
            authorizationLevel: AuthorizationLevel.LIMITED_ACCESS,
            limitedAccessScope: LimitedAccessScope.BANKING,
            accessToRestrictedCapabilities: noAccessToRestrictedCapabilities,
        };
    }

    return null;
};

const useMembershipOnboardingAuthorization: UserAuthorizer = () => {
    const user = useAuthedUser();
    if (!user.membership) {
        return null;
    }

    if (!user.memberJourneyLog?.bookedDiscoverySession) {
        return {
            authorizationLevel: AuthorizationLevel.LIMITED_ACCESS,
            limitedAccessScope: LimitedAccessScope.MEMBERSHIP_ONBOARDING,
            accessToRestrictedCapabilities: noAccessToRestrictedCapabilities,
        };
    }

    return null;
};
